"use client";

import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Switch } from "@/app/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Badge } from "@/app/components/ui/badge";
import { 
  Users, 
  Crown, 
  Building2, 
  User, 
  Sword, 
  RefreshCw, 
  Download,
  Sparkles,
  History,
  Trash2,
  Loader2
} from "lucide-react";
import { toast } from "react-hot-toast";
import { CopyButton } from "@/app/components/ui/copy-button";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";

interface GeneratedName {
  id?: string;
  name: string;
  timestamp: Date;
  generatorType?: string;
}

export type NameGeneratorToolProps = {
  /** `embedded` = inside Generators hub; `page` = standalone layout with hero */
  variant?: "embedded" | "page";
};

export default function NameGeneratorTool({ variant = "embedded" }: NameGeneratorToolProps) {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [generatedNames, setGeneratedNames] = useState<GeneratedName[]>([]);
  const [activeTab, setActiveTab] = useState("human");

  const serverHistoryQuery = trpc.nameGenerator.getNameGeneratorHistory.useQuery(undefined, {
    enabled: !!user,
  });
  const clearServerHistoryMutation = trpc.nameGenerator.clearNameGeneratorHistory.useMutation({
    onSuccess: () => utils.nameGenerator.getNameGeneratorHistory.invalidate(),
  });

  const displayNames = useMemo((): GeneratedName[] => {
    if (user && serverHistoryQuery.data) {
      const out: GeneratedName[] = [];
      for (const batch of serverHistoryQuery.data) {
        const ts =
          batch.createdAt instanceof Date ? batch.createdAt : new Date(batch.createdAt as string);
        batch.names.forEach((name, i) => {
          out.push({
            id: `${batch.id}-${i}`,
            name,
            timestamp: ts,
            generatorType: batch.generatorType,
          });
        });
      }
      return out;
    }
    return generatedNames;
  }, [user, serverHistoryQuery.data, generatedNames]);

  // tRPC mutations
  const generateHumanNames = trpc.nameGenerator.generateHumanNames.useMutation();
  const generateFantasyNames = trpc.nameGenerator.generateFantasyNames.useMutation();
  const generateBusinessNames = trpc.nameGenerator.generateBusinessNames.useMutation();
  const generateUsernames = trpc.nameGenerator.generateUsernames.useMutation();
  const generateCharacterNames = trpc.nameGenerator.generateCharacterNames.useMutation();
  const generateRandomNames = trpc.nameGenerator.generateRandomNames.useMutation();

  // Form states
  const [count, setCount] = useState(10);
  const [gender, setGender] = useState<"male" | "female" | "unisex" | "random">("random");
  const [includeLastName, setIncludeLastName] = useState(true);
  const [format, setFormat] = useState<"first-last" | "last-first" | "first-only">("first-last");
  const [businessStyle, setBusinessStyle] = useState<"prefix-suffix" | "industry-focused" | "creative" | "tech" | "professional">("prefix-suffix");
  const [includeDomain, setIncludeDomain] = useState(false);
  const [usernameStyle, setUsernameStyle] = useState<"random" | "name-based" | "creative" | "gaming" | "professional">("random");
  const [usernameLength, setUsernameLength] = useState(8);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeUnderscores, setIncludeUnderscores] = useState(false);
  const [characterGenre, setCharacterGenre] = useState<"fantasy" | "sci-fi" | "medieval" | "modern" | "steampunk" | "cyberpunk" | "mystical">("fantasy");
  const [includeTitle, setIncludeTitle] = useState(false);
  const [randomMinLength, setRandomMinLength] = useState(3);
  const [randomMaxLength, setRandomMaxLength] = useState(8);
  const [randomPattern, setRandomPattern] = useState<"random" | "vowel-consonant" | "consonant-vowel" | "double-consonant" | "syllable-combo">("random");

  const clearHistory = () => {
    if (user) {
      clearServerHistoryMutation.mutate();
    } else {
      setGeneratedNames([]);
    }
    toast.success("History cleared");
  };

  const downloadNames = () => {
    if (displayNames.length === 0) return;
    
    const content = displayNames.map(item => item.name).join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `generated-names-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Names downloaded!");
  };

  const handleGenerate = async (type: string) => {
    try {
      let result: any;

      switch (type) {
        case "human":
          result = await generateHumanNames.mutateAsync({
            gender,
            count,
            includeLastName,
            format
          });
          break;
        
        case "fantasy":
          result = await generateFantasyNames.mutateAsync({
            gender,
            count,
            includeLastName,
            format
          });
          break;
        
        case "business":
          result = await generateBusinessNames.mutateAsync({
            count,
            style: businessStyle,
            includeDomain
          });
          break;
        
        case "username":
          result = await generateUsernames.mutateAsync({
            count,
            style: usernameStyle,
            length: usernameLength,
            includeNumbers,
            includeUnderscores
          });
          break;
        
        case "character":
          result = await generateCharacterNames.mutateAsync({
            count,
            genre: characterGenre,
            gender,
            includeTitle
          });
          break;
        
        case "random":
          result = await generateRandomNames.mutateAsync({
            count,
            minLength: randomMinLength,
            maxLength: randomMaxLength,
            pattern: randomPattern,
            includeLastName
          });
          break;
        
        default:
          throw new Error("Invalid name type");
      }

      if (user) {
        await utils.nameGenerator.getNameGeneratorHistory.invalidate();
      } else {
        const newNames: GeneratedName[] = result.names.map((name: string) => ({
          name,
          timestamp: new Date(),
        }));
        setGeneratedNames((prev) => [...newNames, ...prev]);
      }
      toast.success(`Generated ${result.count} ${type} names!`);
    } catch (error) {
      console.error("Error generating names:", error);
      toast.error("Failed to generate names. Please try again.");
    }
  };

  const isLoading = generateHumanNames.isPending || generateFantasyNames.isPending || 
                   generateBusinessNames.isPending || generateUsernames.isPending || 
                   generateCharacterNames.isPending || generateRandomNames.isPending;

  const shell = (
    <>
      {variant === "page" && (
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Name Generator</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Generate names for people, characters, businesses, and usernames
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Generator Controls */}
          <div className="lg:col-span-1 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="human" className="text-xs">
                  <Users className="h-4 w-4 mr-1" />
                  Human
                </TabsTrigger>
                <TabsTrigger value="fantasy" className="text-xs">
                  <Crown className="h-4 w-4 mr-1" />
                  Fantasy
                </TabsTrigger>
                <TabsTrigger value="business" className="text-xs">
                  <Building2 className="h-4 w-4 mr-1" />
                  Business
                </TabsTrigger>
                <TabsTrigger value="username" className="text-xs">
                  <User className="h-4 w-4 mr-1" />
                  Username
                </TabsTrigger>
                <TabsTrigger value="character" className="text-xs">
                  <Sword className="h-4 w-4 mr-1" />
                  Character
                </TabsTrigger>
                <TabsTrigger value="random" className="text-xs">
                  <Sparkles className="h-4 w-4 mr-1" />
                  Random
                </TabsTrigger>
              </TabsList>

              {/* Common Settings */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="count">Number of Names</Label>
                    <Input
                      id="count"
                      type="number"
                      min="1"
                      max="50"
                      value={count}
                      onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                    />
                  </div>

                  {(activeTab === "human" || activeTab === "fantasy" || activeTab === "character") && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="gender">Gender</Label>
                        <Select value={gender} onValueChange={(value: any) => setGender(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="random">Random</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="unisex">Unisex</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(activeTab === "human" || activeTab === "fantasy") && (
                        <>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="includeLastName"
                              checked={includeLastName}
                              onCheckedChange={setIncludeLastName}
                            />
                            <Label htmlFor="includeLastName">Include Last Name</Label>
                          </div>

                          {includeLastName && (
                            <div className="space-y-2">
                              <Label htmlFor="format">Name Format</Label>
                              <Select value={format} onValueChange={(value: any) => setFormat(value)}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="first-last">First Last</SelectItem>
                                  <SelectItem value="last-first">Last, First</SelectItem>
                                  <SelectItem value="first-only">First Only</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {activeTab === "business" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="businessStyle">Business Style</Label>
                        <Select value={businessStyle} onValueChange={(value: any) => setBusinessStyle(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prefix-suffix">Prefix + Suffix</SelectItem>
                            <SelectItem value="industry-focused">Industry Focused</SelectItem>
                            <SelectItem value="creative">Creative</SelectItem>
                            <SelectItem value="tech">Tech Style</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="includeDomain"
                          checked={includeDomain}
                          onCheckedChange={setIncludeDomain}
                        />
                        <Label htmlFor="includeDomain">Include Domain</Label>
                      </div>
                    </>
                  )}

                  {activeTab === "username" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="usernameStyle">Username Style</Label>
                        <Select value={usernameStyle} onValueChange={(value: any) => setUsernameStyle(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="random">Random</SelectItem>
                            <SelectItem value="name-based">Name Based</SelectItem>
                            <SelectItem value="creative">Creative</SelectItem>
                            <SelectItem value="gaming">Gaming</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="usernameLength">Length</Label>
                        <Input
                          id="usernameLength"
                          type="number"
                          min="3"
                          max="20"
                          value={usernameLength}
                          onChange={(e) => setUsernameLength(parseInt(e.target.value) || 8)}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="includeNumbers"
                          checked={includeNumbers}
                          onCheckedChange={setIncludeNumbers}
                        />
                        <Label htmlFor="includeNumbers">Include Numbers</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="includeUnderscores"
                          checked={includeUnderscores}
                          onCheckedChange={setIncludeUnderscores}
                        />
                        <Label htmlFor="includeUnderscores">Include Underscores</Label>
                      </div>
                    </>
                  )}

                  {activeTab === "character" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="characterGenre">Genre</Label>
                        <Select value={characterGenre} onValueChange={(value: any) => setCharacterGenre(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fantasy">Fantasy</SelectItem>
                            <SelectItem value="sci-fi">Sci-Fi</SelectItem>
                            <SelectItem value="medieval">Medieval</SelectItem>
                            <SelectItem value="modern">Modern</SelectItem>
                            <SelectItem value="steampunk">Steampunk</SelectItem>
                            <SelectItem value="cyberpunk">Cyberpunk</SelectItem>
                            <SelectItem value="mystical">Mystical</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="includeTitle"
                          checked={includeTitle}
                          onCheckedChange={setIncludeTitle}
                        />
                        <Label htmlFor="includeTitle">Include Title</Label>
                      </div>
                    </>
                  )}

                  {activeTab === "random" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="randomMinLength">Min Length</Label>
                        <Input
                          id="randomMinLength"
                          type="number"
                          min="2"
                          max="10"
                          value={randomMinLength}
                          onChange={(e) => setRandomMinLength(parseInt(e.target.value) || 3)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="randomMaxLength">Max Length</Label>
                        <Input
                          id="randomMaxLength"
                          type="number"
                          min="3"
                          max="15"
                          value={randomMaxLength}
                          onChange={(e) => setRandomMaxLength(parseInt(e.target.value) || 8)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="randomPattern">Pattern</Label>
                        <Select value={randomPattern} onValueChange={(value: any) => setRandomPattern(value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="random">Random</SelectItem>
                            <SelectItem value="vowel-consonant">Vowel-Consonant</SelectItem>
                            <SelectItem value="consonant-vowel">Consonant-Vowel</SelectItem>
                            <SelectItem value="double-consonant">Double Consonant</SelectItem>
                            <SelectItem value="syllable-combo">Syllable Combo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="randomIncludeLastName"
                          checked={includeLastName}
                          onCheckedChange={setIncludeLastName}
                        />
                        <Label htmlFor="randomIncludeLastName">Include Last Name</Label>
                      </div>
                    </>
                  )}

                  <Button
                    onClick={() => handleGenerate(activeTab)}
                    disabled={isLoading}
                    className="w-full btn-enhanced hover-lift"
                  >
                    {isLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Generate Names
                  </Button>
                </CardContent>
              </Card>
            </Tabs>
          </div>

          {/* Generated Names */}
          <div className="lg:col-span-2">
            <Card className="card-enhanced">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Generated Names
                      {displayNames.length > 0 && (
                        <Badge variant="secondary">{displayNames.length}</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      {user
                        ? "History is saved to your account while signed in."
                        : "Names stay in this browser until you sign in."}
                    </CardDescription>
                  </div>
                  {displayNames.length > 0 && (
                    <div className="flex gap-2">
                      <CopyButton
                        text={displayNames.map(item => item.name).join("\n")}
                        variant="outline"
                        size="sm"
                        className="hover-lift"
                        successMessage="All names copied!"
                      >
                        Copy All
                      </CopyButton>
                      <Button
                        onClick={downloadNames}
                        variant="outline"
                        size="sm"
                        className="hover-lift"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        onClick={clearHistory}
                        variant="outline"
                        size="sm"
                        className="hover-lift text-red-600 hover:text-red-700"
                        disabled={clearServerHistoryMutation.isPending}
                      >
                        {clearServerHistoryMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {user && serverHistoryQuery.isLoading ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span>Loading saved names…</span>
                  </div>
                ) : displayNames.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {displayNames.map((item, index) => (
                      <div
                        key={item.id ?? `local-${index}`}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{item.name}</span>
                          {item.generatorType ? (
                            <Badge variant="outline" className="ml-2 align-middle text-[10px] capitalize shrink-0">
                              {item.generatorType}
                            </Badge>
                          ) : null}
                          <span className="text-sm text-muted-foreground ml-2">
                            {item.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        <CopyButton
                          text={item.name}
                          variant="ghost"
                          size="icon"
                          className="hover-lift shrink-0"
                          label="Copy name"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-12">
                    <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>
                      No names yet. Pick a tab and click <span className="font-medium">Generate Names</span>
                      {user ? " — runs are saved to your account." : "."}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </>
  );

  if (variant === "page") {
    return (
      <div className="container mx-auto px-4 py-8 animate-fade-in">
        <div className="max-w-6xl mx-auto">{shell}</div>
      </div>
    );
  }

  return <div className="space-y-6">{shell}</div>;
}
